import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../components/TenantContext.jsx';
import { callsApi, customersApi, tagsApi } from '../api/calls.js';
import { avatarsApi } from '../api/tenants.js';
import { download } from '../api/client.js';
import { useFreshAudioSrc } from '../hooks/useFreshAudioSrc.js';
import { PageHeader } from '../components/ui/PageHeader.jsx';
import { Card, Empty, DirectionChip } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';
import { PhoneCall, ChevronRight, ChevronDown, Play, Pause, ExternalLink, Trash2, Search, X, Download, Tag } from 'lucide-react';

const buildFilters = (t) => [
    { value: '', label: t('common:state.all') },
    { value: 'inbound', label: t('filter.inbound') },
    { value: 'outbound_answered', label: t('filter.outboundAnswered') },
    { value: 'outbound_no_answer', label: t('filter.outboundNoAnswer') },
    { value: 'web', label: t('filter.web') },
    { value: 'web_text', label: t('filter.webText') },
    { value: 'preview', label: t('filter.preview') },
];

const buildDatePresets = (t) => [
    { value: '', label: t('date.any') },
    { value: 'today', label: t('date.today') },
    { value: 'yesterday', label: t('date.yesterday') },
    { value: '7d', label: t('date.last7d') },
    { value: '30d', label: t('date.last30d') },
    { value: 'thisMonth', label: t('date.thisMonth') },
    { value: 'custom', label: t('date.custom') },
];

// preset + 自訂日期 → { fromMillis, toMillis }（毫秒）；不限時間 / 自訂未填齊 → 回 {}
function computeDateRange(preset, customFrom, customTo) {
    const now = new Date();
    const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
    const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x.getTime(); };
    if (preset === 'today') return { fromMillis: startOfDay(now), toMillis: endOfDay(now) };
    if (preset === 'yesterday') {
        const y = new Date(now); y.setDate(y.getDate() - 1);
        return { fromMillis: startOfDay(y), toMillis: endOfDay(y) };
    }
    if (preset === '7d') return { fromMillis: now.getTime() - 7 * 86400000, toMillis: now.getTime() };
    if (preset === '30d') return { fromMillis: now.getTime() - 30 * 86400000, toMillis: now.getTime() };
    if (preset === 'thisMonth') {
        // 當月：本月 1 號 00:00 → 現在
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        return { fromMillis: startOfDay(first), toMillis: now.getTime() };
    }
    if (preset === 'custom') {
        if (!customFrom || !customTo) return {};   // 還沒填齊兩個日期 → 先不套
        return { fromMillis: startOfDay(new Date(customFrom)), toMillis: endOfDay(new Date(customTo)) };
    }
    return {};
}

// 同一通電話的多段記錄（REFER 轉真人失敗接回會產新 session，見 callLog.callGroupId）收合成一列。
// 列表依 startedAt desc → 每組 segments[0] 是最新段、最後一個是第一段。
function groupCalls(items) {
    const groups = [];
    const byKey = new Map();
    for (const it of items) {
        const key = it.callGroupId || it.id;
        const g = byKey.get(key);
        if (g) g.push(it);
        else {
            const arr = [it];
            byKey.set(key, arr);
            groups.push(arr);
        }
    }
    return groups;
}

// 合併列：時間 / 號碼 / 角色用第一段（通話開始），時長加總，結束狀態用最後一段（最終結果）
function mergeSegments(segments) {
    if (segments.length === 1) return segments[0];
    const latest = segments[0];
    const first = segments[segments.length - 1];
    return {
        ...first,
        id: latest.id,
        durationSec: segments.reduce((s, x) => s + (Number(x.durationSec) || 0), 0),
        terminationType: latest.terminationType,
        terminationReason: latest.terminationReason,
        summary: segments.find((x) => x.summary)?.summary || null,
        // 標籤取聯集：標在任何一段上都要顯示。只讀第一段的話，使用者在「轉接失敗接回」
        // 那一段標的標籤會在收合列表裡整個消失（看起來就像標籤沒存成功）。
        tagIds: Array.from(new Set(segments.flatMap((x) => x.tagIds || []))),
        tags: (() => {
            const seen = new Map();
            for (const seg of segments) for (const tg of (seg.tags || [])) {
                if (tg && !seen.has(tg.id || tg.name)) seen.set(tg.id || tg.name, tg);
            }
            return [...seen.values()];
        })(),
        _segments: segments,
    };
}

export default function CallsPage() {
    const { t, i18n } = useTranslation('calls');
    // 超管路徑 /admin/tenants/:hostId/calls 帶 route param 覆寫；一般用戶走 TenantContext
    const { hostId: routeHostId } = useParams();
    const { hostId: ctxHostId, isAdmin } = useTenant();
    const hostId = routeHostId || ctxHostId;
    const FILTERS = buildFilters(t);
    const DATE_PRESETS = buildDatePresets(t);
    const [filter, setFilter] = useState('');
    const [items, setItems] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    // 日期範圍篩選
    const [datePreset, setDatePreset] = useState('');     // '' | today | yesterday | 7d | 30d | thisMonth | custom
    const [customFrom, setCustomFrom] = useState('');     // 'YYYY-MM-DD'
    const [customTo, setCustomTo] = useState('');
    // 角色篩選：'' = 全部。後端 /list 與 /export.xlsx 都已支援 avatarId，這裡只是接上去。
    const [avatarId, setAvatarId] = useState('');
    const [avatars, setAvatars] = useState([]);
    // 電話號碼查詢：phoneInput 是輸入框即時值，按下搜尋（或 Enter）才套用成 phoneQuery
    const [phoneInput, setPhoneInput] = useState('');
    const [phoneQuery, setPhoneQuery] = useState('');
    // 匯出 Excel
    const [exporting, setExporting] = useState(false);
    // phone → { firstCallAt, totalCount } 用來判 repeat caller
    const [phoneFirstCallMap, setPhoneFirstCallMap] = useState(new Map());
    // 標籤對照表 id → { name, color }。列表本來就有 call.tagIds,只是沒地方查名字跟顏色
    // （0805 東訊回報：加了標籤在列表看不到,但下載 Excel 有）。
    const [tagMap, setTagMap] = useState(new Map());

    // 該租戶全部角色（含目前沒有通話紀錄的）—— 選到空的也是有用的資訊：「這隻真的沒人打」
    useEffect(() => {
        if (!hostId) return;
        let cancelled = false;
        avatarsApi.listForTenant(hostId)
            .then((r) => { if (!cancelled) setAvatars(r.avatars || []); })
            .catch(() => { if (!cancelled) setAvatars([]); });
        return () => { cancelled = true; };
    }, [hostId]);

    // 換租戶時清掉選中的角色，避免帶著別家的 avatarId 查詢
    useEffect(() => { setAvatarId(''); }, [hostId]);

    useEffect(() => {
        if (!hostId) return;
        let cancelled = false;
        tagsApi.list(hostId)
            .then((r) => {
                if (cancelled) return;
                const m = new Map();
                for (const tg of (r.data || r.items || [])) m.set(tg.id, tg);
                setTagMap(m);
            })
            .catch(() => { /* 標籤拿不到就不顯示,不影響列表 */ });
        return () => { cancelled = true; };
    }, [hostId]);

    useEffect(() => {
        if (!hostId) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await customersApi.list(hostId, { limit: 800 });
                if (cancelled) return;
                const m = new Map();
                for (const c of r.items || []) {
                    m.set(String(c.phone), {
                        firstCallAt: c.firstCallAt,
                        totalCount: c.totalCount,
                    });
                }
                setPhoneFirstCallMap(m);
            } catch (e) {
                // 不擋主流程；repeat tag 沒出來就算了
                console.warn('customers/list for repeat detection failed:', e.message);
            }
        })();
        return () => { cancelled = true; };
    }, [hostId]);

    // 前端過濾（網頁 / 網頁文字 / 模擬通話 是 source 標記，不是 direction）：
    //   filter='preview'  → 只留 source='ivr_test'
    //   filter='web'      → 只留 source='web'（網頁語音）
    //   filter='web_text' → 只留 source='web_text'（網頁文字客服）
    //   全部              → 電話 + 網頁語音 + 網頁文字都看得到，只排除模擬通話（ivr_test，工作室測試）
    //   inbound / outbound* → 只看電話：排除 ivr_test + web + web_text
    function sourceFilter(it) {
        // ✅ 一律排除問卷通話（survey avatar 的進線/外撥不歸通話記錄管，歸問卷管理頁）
        if (it.sourceLabel === 'survey') return false;
        const isPreview = it.source === 'ivr_test';
        const isWeb = it.source === 'web';
        const isWebText = it.source === 'web_text';
        if (filter === 'preview') return isPreview;
        if (filter === 'web') return isWeb;
        if (filter === 'web_text') return isWebText;
        if (filter === '') return !isPreview;
        return !isPreview && !isWeb && !isWebText;
    }

    // 因為 source 是前端過濾，後端送來的 30 筆可能大半被濾掉（譬如 30 raw 只剩 1 筆真實電話）
    // → 自動多撈幾頁 raw，直到累積到 ≥ 30 筆過濾後的結果才停（上限 10 頁 = 300 raw 保護）
    const PAGE_TARGET = 30;
    const MAX_FETCH_PAGES = 10;
    async function load(reset = false) {
        if (!hostId) return;
        setLoading(true);
        setError(null);
        try {
            const baseParams = { limit: 30 };
            if (filter === 'inbound') baseParams.direction = 'inbound';
            else if (filter === 'outbound_answered') { baseParams.direction = 'outbound'; baseParams.wasAnswered = 'true'; }
            else if (filter === 'outbound_no_answer') { baseParams.direction = 'outbound'; baseParams.wasAnswered = 'false'; }
            // 網頁 / 網頁文字 / 模擬通話 交給後端 where 過濾（sourceFilter 仍會再過一次，結果一樣）。
            // 沒下推之前，電話量大的租戶這三個永遠空白：前端最多只抓 30×10=300 筆再自己濾，
            // 而 ipcc 8,152 筆裡最新的 ivr_test 排在第 1,432 筆，撈不到。
            else if (filter === 'web' || filter === 'web_text' || filter === 'preview') baseParams.source = filter;
            const { fromMillis, toMillis } = computeDateRange(datePreset, customFrom, customTo);
            if (fromMillis) baseParams.fromMillis = fromMillis;
            if (toMillis) baseParams.toMillis = toMillis;
            if (phoneQuery) baseParams.phone = phoneQuery;
            // 下推給後端（listPaged 的 where avatarId ==）。前端濾不行：只抓 30×10 筆，
            // 通話量大的租戶選冷門角色會一片空白 —— 跟 web/web_text 當初同一個坑。
            if (avatarId) baseParams.avatarId = avatarId;

            let accumulated = [];
            let nextCursor = reset ? null : cursor;
            let pages = 0;
            while (accumulated.length < PAGE_TARGET && pages < MAX_FETCH_PAGES) {
                const params = { ...baseParams };
                if (nextCursor) params.cursor = nextCursor;
                const r = await callsApi.list(hostId, params);
                const filtered = (r.items || []).filter(sourceFilter);
                accumulated = [...accumulated, ...filtered];
                nextCursor = r.nextCursor;
                pages++;
                if (!nextCursor) break;
            }
            const newItems = reset ? accumulated : [...items, ...accumulated];
            setItems(newItems);
            setCursor(nextCursor);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        setItems([]);
        setCursor(null);
        load(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostId, filter, datePreset, customFrom, customTo, phoneQuery, avatarId]);

    // 匯出 Excel：帶當前畫面篩選（類型/時間/電話），欄位含逐字稿與錄音連結
    async function handleExport() {
        if (!hostId || exporting) return;
        setExporting(true);
        try {
            // tz：匯出時間跟畫面一樣用瀏覽器時區（泰國 UTC+7，後端預設台北會快 1 小時）
            const params = { lang: i18n.language, tz: Intl.DateTimeFormat().resolvedOptions().timeZone };
            // filter chip → direction / wasAnswered / source（與列表過濾一致）
            if (filter === 'inbound') params.direction = 'inbound';
            else if (filter === 'outbound_answered') { params.direction = 'outbound'; params.wasAnswered = 'true'; }
            else if (filter === 'outbound_no_answer') { params.direction = 'outbound'; params.wasAnswered = 'false'; }
            // 「全部」= 模擬通話以外的全部（non_preview）；進線 / 外撥 = 只有電話
            params.source = filter === 'web' ? 'web' : filter === 'web_text' ? 'web_text' : filter === 'preview' ? 'preview'
                : filter === '' ? 'non_preview' : 'phone';
            const { fromMillis, toMillis } = computeDateRange(datePreset, customFrom, customTo);
            if (fromMillis) params.fromMillis = fromMillis;
            if (toMillis) params.toMillis = toMillis;
            if (phoneQuery) params.phone = phoneQuery;
            if (avatarId) params.avatarId = avatarId;   // 匯出跟畫面一致：看到什麼就匯出什麼
            await download(callsApi.exportUrl(hostId, params));
        } catch (e) {
            alert(t('toast.exportFailed', { msg: e.message }));
        } finally {
            setExporting(false);
        }
    }

    return (
        <div>
            <PageHeader
                eyebrow={hostId ? `tenant · ${hostId}` : 'tenant'}
                title={t('page.title')}
                description={t('page.description')}
            />

            {/* Filter chips + 匯出 */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
                {FILTERS.map((f) => (
                    <button
                        key={f.value}
                        onClick={() => setFilter(f.value)}
                        className={[
                            'px-3 py-1 rounded-full text-xs font-medium transition',
                            filter === f.value
                                ? 'bg-ink-900 text-cream-50'
                                : 'bg-cream-100 text-ink-700 hover:bg-cream-200',
                        ].join(' ')}
                    >
                        {f.label}
                    </button>
                ))}
                <div className="ml-auto">
                    <Button size="sm" variant="outline" onClick={handleExport} disabled={exporting || !hostId}>
                        <Download size={14} />
                        {exporting ? t('export.running') : t('export.button')}
                    </Button>
                </div>
            </div>

            {/* Date range filter */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-xs text-ink-400 mr-1">{t('date.label')}</span>
                {DATE_PRESETS.map((d) => (
                    <button
                        key={d.value}
                        onClick={() => setDatePreset(d.value)}
                        className={[
                            'px-3 py-1 rounded-full text-xs font-medium transition',
                            datePreset === d.value
                                ? 'bg-accent-700 text-cream-50'
                                : 'bg-cream-100 text-ink-700 hover:bg-cream-200',
                        ].join(' ')}
                    >
                        {d.label}
                    </button>
                ))}
                {/* 角色篩選器：列該租戶全部角色（含目前 0 通的 —— 選到空的也是有用的答案） */}
                <div className="flex items-center gap-1.5 ml-auto">
                    <span className="text-xs text-ink-400">{t('avatar.label')}</span>
                    <select
                        value={avatarId}
                        onChange={(e) => setAvatarId(e.target.value)}
                        disabled={loading}
                        className="px-2.5 py-1 text-xs border border-cream-300 rounded-md bg-white text-ink-700 focus:outline-none focus:border-accent-400 disabled:opacity-50 disabled:cursor-not-allowed max-w-[220px]"
                    >
                        <option value="">{t('avatar.all')}</option>
                        {avatars.map((a) => (
                            <option key={a.avatarId || a.id} value={a.avatarId || a.id}>
                                {a.name || a.avatarName || (a.avatarId || a.id)}
                            </option>
                        ))}
                    </select>
                </div>
                {datePreset === 'custom' && (
                    <div className="flex items-center gap-1.5 ml-1">
                        <input
                            type="date"
                            value={customFrom}
                            max={customTo || undefined}
                            onChange={(e) => setCustomFrom(e.target.value)}
                            className="px-2 py-1 text-xs border border-cream-300 rounded-md bg-white text-ink-700 focus:outline-none focus:border-accent-400"
                        />
                        <span className="text-xs text-ink-400">{t('date.to')}</span>
                        <input
                            type="date"
                            value={customTo}
                            min={customFrom || undefined}
                            onChange={(e) => setCustomTo(e.target.value)}
                            className="px-2 py-1 text-xs border border-cream-300 rounded-md bg-white text-ink-700 focus:outline-none focus:border-accent-400"
                        />
                    </div>
                )}
                {/* 查詢：完整電話號碼 or SessionID（後端依形狀自動分流），按 Enter 或放大鏡 */}
                {/* ⚠️ 不設 inputMode="tel" —— 會讓手機跳數字鍵盤，貼 SessionID 很難用 */}
                <div className="ml-auto flex items-center gap-1.5">
                    <div className="relative">
                        <input
                            type="text"
                            value={phoneInput}
                            placeholder={t('phoneSearch.placeholder')}
                            onChange={(e) => setPhoneInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') setPhoneQuery(phoneInput.trim());
                            }}
                            className="w-60 pl-2 pr-7 py-1 text-xs border border-cream-300 rounded-md bg-white text-ink-700 focus:outline-none focus:border-accent-400 tnum"
                        />
                        {phoneQuery ? (
                            <button
                                onClick={() => { setPhoneInput(''); setPhoneQuery(''); }}
                                title={t('phoneSearch.clear')}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-ink-400 hover:text-danger-500 transition"
                            >
                                <X size={13} />
                            </button>
                        ) : (
                            <button
                                onClick={() => setPhoneQuery(phoneInput.trim())}
                                title={t('phoneSearch.search')}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-ink-400 hover:text-accent-700 transition"
                            >
                                <Search size={13} />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {error && (
                <Card className="bg-danger-100/40 border-danger-500/30 mb-4">
                    <div className="text-sm text-danger-500">{error.message}</div>
                </Card>
            )}

            {/* cursor 還在代表後面還有沒掃到的資料 → 不能只給空狀態，否則使用者按不到「載入更多」被鎖死 */}
            {items.length === 0 && !loading && !cursor ? (
                <Card>
                    <Empty
                        icon={PhoneCall}
                        title={t('empty.title')}
                        description={t('empty.description')}
                    />
                </Card>
            ) : (
                <Card padded={false}>
                    <table className="w-full">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-ink-400 font-mono">
                                <th className="text-left px-4 py-2.5 font-medium">{t('col.time')}</th>
                                <th className="text-left px-4 py-2.5 font-medium">{t('col.direction')}</th>
                                <th className="text-left px-4 py-2.5 font-medium">{t('col.peerNumber')}</th>
                                <th className="text-left px-4 py-2.5 font-medium">{t('col.aiAgent')}</th>
                                <th className="text-left px-4 py-2.5 font-medium">{t('col.didExt')}</th>
                                <th className="text-right px-4 py-2.5 font-medium">{t('col.duration')}</th>
                                <th className="text-left px-4 py-2.5 font-medium">{t('col.termination')}</th>
                                <th className="text-left px-4 py-2.5 font-medium">{t('col.tags')}</th>
                                <th className="text-left px-4 py-2.5 font-medium">{t('col.summary')}</th>
                                <th className="w-8"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupCalls(items).map((segs) => {
                                const it = mergeSegments(segs);
                                return (
                                <CallRow
                                    key={it.id}
                                    call={it}
                                    tagMap={tagMap}
                                    phoneFirstCallMap={phoneFirstCallMap}
                                    expanded={expandedId === it.id}
                                    onToggle={async () => {
                                        const next = expandedId === it.id ? null : it.id;
                                        setExpandedId(next);
                                        // lazy-load transcript on expand(list 端為了加速已砍 transcript)
                                        // 多段收合列 → 每段各自補 detail
                                        if (next === it.id) {
                                            const targets = segs.filter((s) => !s.transcript);
                                            await Promise.all(targets.map(async (s) => {
                                                try {
                                                    const r = await callsApi.get(hostId, s.id);
                                                    if (r?.data) {
                                                        setItems((cur) => cur.map((x) => (x.id === s.id ? { ...x, ...r.data } : x)));
                                                    }
                                                } catch (e) {
                                                    console.warn('[CallsPage] fetch detail failed:', e?.message);
                                                }
                                            }));
                                        }
                                    }}
                                    onDelete={isAdmin ? async () => {
                                        const msg = segs.length > 1
                                            ? t('dialog.deleteConfirmGroup', { count: segs.length, peer: it.callerNumber || it.calleeNumber || '', duration: formatDuration(it.durationSec) })
                                            : t('dialog.deleteConfirmRow', { peer: it.callerNumber || it.calleeNumber || '', duration: formatDuration(it.durationSec) });
                                        if (!confirm(msg)) return;
                                        try {
                                            // 錄音現在會一併刪除。刪不掉不擋記錄刪除,但要如實告知 ——
                                            // 使用者按了「刪除」就是要它消失,不能讓畫面說成功而錄音還在。
                                            const failed = [];
                                            for (const s of segs) {
                                                const r = await callsApi.remove(hostId, s.id);
                                                if (r && r.recordingDeleted === false) failed.push(r.recordingError || '');
                                            }
                                            if (failed.length) alert(t('toast.recordingDeleteFailed', { msg: failed[0] }));
                                            const ids = new Set(segs.map((s) => s.id));
                                            setItems((cur) => cur.filter((x) => !ids.has(x.id)));
                                            if (expandedId === it.id) setExpandedId(null);
                                        } catch (e) {
                                            alert(t('toast.deleteFailed', { msg: e.message }));
                                        }
                                    } : undefined}
                                />
                                );
                            })}
                        </tbody>
                    </table>
                    {(loading || cursor) && (
                        <div className="border-t border-cream-200 px-4 py-3 flex justify-center">
                            {loading ? (
                                <div className="text-xs text-ink-400">{t('common:state.loading')}</div>
                            ) : cursor ? (
                                <Button size="sm" variant="ghost" onClick={() => load(false)}>
                                    {t('loadMore')}
                                </Button>
                            ) : null}
                        </div>
                    )}
                </Card>
            )}
        </div>
    );
}

// 列表的標籤 chips：最多顯示 2 個,其餘收成 +N（標籤多的時候不要把欄位撐爆）。
// 資料來源優先 call.tags（callSummarizer 寫的完整物件）,沒有就用 call.tagIds 去 tagMap 查。
function CallTags({ call, tagMap }) {
    const { t } = useTranslation('calls');
    const list = Array.isArray(call.tags) && call.tags.length
        ? call.tags
        : (call.tagIds || []).map((id) => tagMap?.get(id)).filter(Boolean);
    if (!list.length) return <span className="text-xs italic text-ink-300">—</span>;
    const shown = list.slice(0, 2);
    const rest = list.length - shown.length;
    return (
        <div className="flex flex-wrap items-center gap-1">
            {shown.map((tg, i) => (
                <span
                    key={tg.id || i}
                    title={tg.name}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium border max-w-[9rem]"
                    style={tg.color
                        ? { background: tg.color, borderColor: tg.color, color: '#fff' }
                        : undefined}
                >
                    <Tag size={9} className="shrink-0" />
                    <span className="truncate">{tg.name}</span>
                </span>
            ))}
            {rest > 0 && (
                <span className="text-[11px] text-ink-400" title={list.map((x) => x.name).join('、')}>
                    {t('tags.more', { count: rest })}
                </span>
            )}
        </div>
    );
}

function CallRow({ call, phoneFirstCallMap, tagMap, expanded, onToggle, onDelete }) {
    const { t } = useTranslation('calls');
    const navigate = useNavigate();
    const { hostId: routeHostId } = useParams();   // 超管路徑 → 詳情頁也走 /admin 前綴
    const startedAt = call.startedAt?._seconds
        ? new Date(call.startedAt._seconds * 1000)
        : null;
    // 重覆來電：對方號碼曾在更早時間點打過 → 標 "之前已打過"
    const otherPhone = call.direction === 'outbound'
        ? (call.calleeNumber || call.callerNumber)
        : (call.callerNumber || call.callerPhone);
    const customerSummary = otherPhone ? phoneFirstCallMap?.get(String(otherPhone)) : null;
    const isRepeat = !!(
        customerSummary
        && customerSummary.totalCount >= 2
        && startedAt
        && customerSummary.firstCallAt
        && startedAt.getTime() > customerSummary.firstCallAt
    );

    return (
        <>
        <tr
            className={[
                'border-t border-cream-100 hover:bg-cream-50 group cursor-pointer',
                expanded ? 'bg-cream-50' : '',
            ].join(' ')}
            onClick={onToggle}
        >
            <td className="px-4 py-3 align-top">
                <div className="text-sm text-ink-900 tnum">
                    {startedAt
                        ? startedAt.toLocaleString('zh-TW', {
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                          })
                        : '—'}
                </div>
                <div className="mono-caption">{startedAt?.toLocaleDateString('zh-TW', { year: 'numeric' })}</div>
            </td>
            <td className="px-4 py-3 align-top">
                <DirectionChip direction={call.direction} />
            </td>
            <td className="px-4 py-3 align-top tnum">
                <div className="flex items-center gap-2">
                    {call.source === 'ivr_test' ? (
                        <span className="text-xs text-ink-500 whitespace-nowrap">{t('filter.preview')}</span>
                    ) : call.source === 'web' ? (
                        <span
                            className="text-xs text-ink-500 whitespace-nowrap"
                            title={call.webUid ? t('webVisitor.tooltipUid', { uid: call.webUid }) : t('webVisitor.tooltip')}
                        >
                            {t('webVisitor.label')}{call.webUid ? ` ${call.webUid.slice(0, 6)}` : ''}
                        </span>
                    ) : call.source === 'web_text' ? (
                        <span
                            className="text-xs text-ink-500 whitespace-nowrap"
                            title={call.webUid ? t('webVisitor.tooltipUid', { uid: call.webUid }) : t('webVisitor.tooltip')}
                        >
                            {t('webTextVisitor.label')}{call.webUid ? ` ${call.webUid.slice(0, 6)}` : ''}
                        </span>
                    ) : (
                        <span className="text-sm text-ink-900">
                            {call.callerNumber || call.calleeNumber || '—'}
                        </span>
                    )}
                    {isRepeat && (
                        <span
                            className="inline-flex items-center px-1.5 py-0.5 bg-accent-50 text-accent-700 text-[9px] rounded font-mono font-medium tracking-wider"
                            title={t('repeat.tooltip', { count: customerSummary.totalCount })}
                        >
                            REPEAT
                        </span>
                    )}
                </div>
                {call.ext && call.ext !== call.callerNumber && call.source !== 'ivr_test' && (
                    <div className="mono-caption">trunk {call.ext}</div>
                )}
            </td>
            <td className="px-4 py-3 align-top">
                <div className="text-sm text-ink-700 truncate max-w-[140px]">
                    {call.avatarName || '—'}
                </div>
                {call.avatarId && (
                    <div className="mono-caption">{call.avatarId.slice(0, 8)}…</div>
                )}
            </td>
            <td className="px-4 py-3 align-top">
                <div className="text-sm text-ink-700 tnum">{call.did || '—'}</div>
                {call.ext && <div className="mono-caption">ext {call.ext}</div>}
            </td>
            <td className="px-4 py-3 align-top text-right">
                <div className="text-sm text-ink-900 tnum">
                    {formatDuration(call.durationSec)}
                </div>
                {call._segments && (
                    <div
                        className="inline-flex items-center px-1.5 py-0.5 mt-0.5 bg-cream-200 text-ink-500 text-[9px] rounded font-mono font-medium tracking-wider whitespace-nowrap"
                        title={t('segments.tooltip', { count: call._segments.length })}
                    >
                        {t('segments.badge', { count: call._segments.length })}
                    </div>
                )}
            </td>
            <td className="px-4 py-3 align-top">
                <span className="text-xs text-ink-500">
                    {translateTermType(call.terminationType, t)}
                </span>
            </td>
            <td className="px-4 py-3 align-top">
                <CallTags call={call} tagMap={tagMap} />
            </td>
            <td className="px-4 py-3 align-top max-w-xs">
                <div className="text-xs text-ink-500 line-clamp-2">
                    {call.summary || (
                        <span className="italic text-ink-300">—</span>
                    )}
                </div>
            </td>
            <td className="px-4 py-3 align-top">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(routeHostId ? `/admin/tenants/${routeHostId}/calls/${call.id}` : `/calls/${call.id}`);
                    }}
                    title={t('action.openFullDetail')}
                    className="inline-flex p-1.5 rounded hover:bg-cream-200 text-ink-400 hover:text-accent-700 transition"
                >
                    <ChevronRight size={16} />
                </button>
            </td>
        </tr>
        {expanded && (
            <tr className="bg-cream-100/50 border-t border-cream-200">
                <td colSpan={10} className="px-6 py-5">
                    {call._segments
                        ? <ExpandedGroup call={call} onDelete={onDelete} />
                        : <ExpandedCall call={call} onDelete={onDelete} />}
                </td>
            </tr>
        )}
        </>
    );
}

// 多段收合列展開：依時間順序列出每段（第一段 = 通話開始；後續段 = 轉接失敗被 AI 接回）
function ExpandedGroup({ call, onDelete }) {
    const { t } = useTranslation('calls');
    const chrono = [...call._segments].reverse();
    return (
        <div className="relative space-y-6">
            {onDelete && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    title={t('action.deleteRecord')}
                    className="absolute top-0 right-0 inline-flex items-center gap-1 px-2 py-1 text-[11px] text-danger-500 bg-white border border-cream-300 rounded hover:bg-danger-100/40 transition"
                >
                    <Trash2 size={12} />
                    {t('common:actions.delete')}
                </button>
            )}
            <div className="text-xs text-ink-500">{t('segments.note', { count: chrono.length })}</div>
            {chrono.map((seg, i) => {
                const st = seg.startedAt?._seconds ? new Date(seg.startedAt._seconds * 1000) : null;
                return (
                    <div key={seg.id} className={i > 0 ? 'border-t border-cream-200 pt-5' : ''}>
                        <div className="flex items-center gap-2 mb-3">
                            <span className="font-mono text-[10px] uppercase tracking-wider bg-cream-200 text-ink-700 px-2 py-0.5 rounded">
                                {t('segments.header', { n: i + 1 })}
                            </span>
                            <span className="text-xs text-ink-400 tnum">
                                {st ? st.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                                {' · '}
                                {formatDuration(seg.durationSec)}
                                {' · '}
                                {translateTermType(seg.terminationType, t)}
                            </span>
                        </div>
                        <ExpandedCall call={seg} />
                    </div>
                );
            })}
        </div>
    );
}

function ExpandedCall({ call, onDelete }) {
    const { t } = useTranslation('calls');
    return (
        <div className="grid grid-cols-3 gap-6 relative">
            {onDelete && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    title={t('action.deleteRecord')}
                    className="absolute top-0 right-0 inline-flex items-center gap-1 px-2 py-1 text-[11px] text-danger-500 bg-white border border-cream-300 rounded hover:bg-danger-100/40 transition"
                >
                    <Trash2 size={12} />
                    {t('common:actions.delete')}
                </button>
            )}
            <div className="col-span-2 space-y-4">
                {/* Audio */}
                {call.audioUrl ? (
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2">
                            {t('section.recording')}
                        </div>
                        <InlineAudioPlayer src={call.audioUrl} callId={call.id} />
                    </div>
                ) : (
                    <div className="text-xs text-ink-400 italic">
                        {t('recording.pending')}
                    </div>
                )}

                {/* Transcript：有 fullTranscript（錄音轉錄乾淨全程版，3 角色）優先；否則即時版 */}
                {(call.fullTranscript?.length > 0 || call.transcript?.length > 0 || call.transferTranscript?.length > 0) ? (
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-400">
                                {t('transcript.withCount', {
                                    count: call.fullTranscript?.length > 0
                                        ? call.fullTranscript.length
                                        : (call.transcript?.length || 0) + (call.transferTranscript?.length || 0),
                                })}
                            </span>
                            <span className={[
                                'inline-block text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap',
                                call.fullTranscript?.length > 0
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : 'bg-amber-50 text-amber-700 border border-amber-200',
                            ].join(' ')}>
                                {call.fullTranscript?.length > 0 ? t('transcript.statusClean') : t('transcript.statusLive')}
                            </span>
                        </div>
                        <div className="bg-white border border-cream-200 rounded-md p-4 max-h-72 overflow-y-auto space-y-2">
                            {call.fullTranscript?.length > 0 ? (
                                call.fullTranscript.map((tr, i) => (
                                    <TranscriptRow key={`f${i}`} role={tr.role} text={tr.text} />
                                ))
                            ) : (
                                <>
                                    {(call.transcript || []).map((tr, i) => (
                                        <TranscriptRow key={`a${i}`} role={tr.role === 'user' ? 'user' : 'ai'} text={tr.text} />
                                    ))}
                                    {call.transferTranscript?.length > 0 && (
                                        <>
                                            <div className="flex items-center gap-2 py-1 text-[10px] font-mono uppercase tracking-wider text-emerald-600/80">
                                                <span className="flex-1 h-px bg-emerald-200" />
                                                {t('transcript.afterTransfer')}
                                                <span className="flex-1 h-px bg-emerald-200" />
                                            </div>
                                            {call.transferTranscript.map((tr, i) => (
                                                <TranscriptRow key={`t${i}`} role={tr.role === 'agent' ? 'agent' : 'caller'} text={tr.text} />
                                            ))}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="text-xs text-ink-400 italic">{t('transcript.none')}</div>
                )}
            </div>

            <div className="space-y-4">
                {/* Summary */}
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2">
                        {t('section.aiSummary')}
                    </div>
                    {call.summary ? (
                        <p className="text-sm text-ink-700 leading-relaxed whitespace-pre-wrap">
                            {call.summary}
                        </p>
                    ) : (
                        <div className="text-xs text-ink-400 italic">{t('summary.notGenerated')}</div>
                    )}
                </div>

                {/* Quick metadata */}
                <div className="border-t border-cream-200 pt-3 space-y-1.5 text-xs">
                    <Meta label="sessionId" mono value={call.sessionId} />
                    {(() => {
                        const r = translateTermReason(call.terminationReason, call.terminationType, t);
                        return r ? <Meta label={t('meta.terminationReason')} value={r} /> : null;
                    })()}
                    <Meta label={t('col.aiAgent')} value={call.avatarName} />
                </div>
            </div>
        </div>
    );
}

const TRANSCRIPT_ROLE_CLS = {
    user: 'text-accent-700',
    ai: 'text-ink-400',
    caller: 'text-accent-700',
    agent: 'text-emerald-600',
};

function transcriptRoleLabel(role, t) {
    if (role === 'caller') return t('role.caller');
    if (role === 'agent') return t('role.agent');
    if (role === 'user') return 'user';
    return 'ai';
}

function TranscriptRow({ role, text }) {
    const { t } = useTranslation('calls');
    const cls = TRANSCRIPT_ROLE_CLS[role] || TRANSCRIPT_ROLE_CLS.ai;
    return (
        <div className="flex gap-3 text-sm">
            <span className={['shrink-0 w-12 font-mono text-[10px] uppercase tracking-wide mt-0.5', cls].join(' ')}>
                {transcriptRoleLabel(role, t)}
            </span>
            <span className="text-ink-700">{text}</span>
        </div>
    );
}

function Meta({ label, value, mono }) {
    return (
        <div className="flex items-baseline gap-2">
            <span className="font-mono text-[9px] uppercase tracking-wider text-ink-400 w-20 shrink-0">
                {label}
            </span>
            <span className={['truncate text-ink-700', mono ? 'font-mono text-[10px]' : ''].join(' ')} title={value}>
                {value || '—'}
            </span>
        </div>
    );
}

function InlineAudioPlayer({ src, callId }) {
    const { t } = useTranslation('calls');
    const audioRef = useRef(null);
    // 錄音網址 10 分鐘過期（SRA R11）→ 播放出錯就重拿這通的詳情換新網址
    const { hostId: routeHostId } = useParams();
    const { hostId: ctxHostId } = useTenant();
    const hostId = routeHostId || ctxHostId;
    const fresh = useFreshAudioSrc(src, callId && hostId
        ? () => callsApi.get(hostId, callId).then((r) => r?.data?.audioUrl || null)
        : null);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [rate, setRate] = useState(1);
    return (
        <div className="bg-white border border-cream-200 rounded-md px-3 py-2.5 flex items-center gap-3">
            <audio
                ref={audioRef}
                src={fresh.src}
                onError={fresh.onError}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onTimeUpdate={(e) => setProgress(e.target.currentTime)}
                onLoadedMetadata={(e) => { setDuration(e.target.duration); fresh.onLoadedMetadata(e); }}
                preload="metadata"
            />
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    const a = audioRef.current;
                    if (!a) return;
                    if (a.paused) a.play(); else a.pause();
                }}
                className="size-9 rounded-full bg-accent-500 text-white grid place-items-center hover:bg-accent-700 transition shrink-0"
            >
                {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
            </button>
            <div className="flex-1">
                <div
                    className="h-3 flex items-center group cursor-pointer select-none"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!duration || !audioRef.current) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
                        audioRef.current.currentTime = ratio * duration;
                    }}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        if (!duration || !audioRef.current) return;
                        const bar = e.currentTarget;
                        const seek = (clientX) => {
                            const rect = bar.getBoundingClientRect();
                            const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
                            if (audioRef.current) audioRef.current.currentTime = ratio * duration;
                        };
                        seek(e.clientX);
                        const onMove = (ev) => seek(ev.clientX);
                        const onUp = () => {
                            window.removeEventListener('mousemove', onMove);
                            window.removeEventListener('mouseup', onUp);
                        };
                        window.addEventListener('mousemove', onMove);
                        window.addEventListener('mouseup', onUp);
                    }}
                >
                    <div className="relative w-full h-1.5 bg-cream-200 rounded-full overflow-hidden group-hover:h-2 transition-[height]">
                        <div
                            className="h-full bg-accent-500"
                            style={{ width: duration ? `${(progress / duration) * 100}%` : 0 }}
                        />
                    </div>
                </div>
                <div className="flex justify-between mt-0.5 mono-caption">
                    <span>{fmt(progress)}</span>
                    <span>{fmt(duration)}</span>
                </div>
            </div>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    const next = rate === 1 ? 1.25 : rate === 1.25 ? 1.5 : rate === 1.5 ? 2 : 1;
                    setRate(next);
                    if (audioRef.current) audioRef.current.playbackRate = next;
                }}
                className="font-mono text-xs px-2 py-1 bg-cream-100 hover:bg-cream-200 text-ink-700 rounded transition tnum shrink-0"
            >
                {rate}×
            </button>
            <a
                href={src}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                title={t('audio.openInNewTab')}
                className="text-ink-400 hover:text-accent-700 transition"
            >
                <ExternalLink size={14} />
            </a>
        </div>
    );
}

function fmt(sec) {
    if (!sec) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
}

function formatDuration(sec) {
    if (!sec || sec < 0) return '—';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

function translateTermType(type, t) {
    const keys = {
        normal: 'termType.normal',
        transfer: 'termType.transfer',
        end_call: 'termType.endCall',
        user_hangup: 'termType.userHangup',
        unexpected: 'termType.unexpected',
        ANSWER: 'termType.answer',
        NOANSWER: 'termType.noAnswer',
        BUSY: 'termType.busy',
        CANCEL: 'termType.cancel',
        unknown: 'termType.unknown',
        license_expired: 'termType.licenseExpired',   // 東訊 SDX500 授權到期、只播到期語音
        quota_exhausted: 'termType.quotaExhausted',   // 預付帳號分鐘數用完、只播停用語音（不計費）
    };
    return keys[type] ? t(keys[type]) : (type || '—');
}

/**
 * 翻譯 terminationReason；跟 type 重複（user_hangup ↔ sip_socket_close 等）就回空字串隱藏
 */
export function translateTermReason(reason, type, t) {
    if (!reason) return '';
    // type 已經表達同樣意思時不重複顯示
    const redundant = {
        user_hangup: ['socket_close', 'sip_socket_close', 'sip_hangup', 'sip_bye', 'twilio_stop', 'mark_echo', 'client_left'],
        end_call: ['terminal_api_completed'],
    };
    if (redundant[type]?.includes(reason)) return '';

    const keys = {
        // AI tool call reasons
        user_done: 'termReason.userDone',
        task_complete: 'termReason.taskComplete',
        goodbye: 'termReason.goodbye',
        hangup_during_closing: 'termReason.hangupDuringClosing',
        // transfer reasons
        transferred_human: 'termReason.transferredHuman',
        transferred_failed: 'termReason.transferredFailed',
        controller_transfer: 'termReason.controllerTransfer',
        transfer_failed: 'termReason.transferFailed',
        // 系統錯誤
        socket_close: 'termReason.socketClose',
        sip_socket_close: 'termReason.sipSocketClose',
        sip_hangup: 'termReason.sipHangup',
        sip_bye: 'termReason.sipBye',
        twilio_stop: 'termReason.twilioStop',
        mark_echo: 'termReason.markEcho',
    };
    return keys[reason] ? t(keys[reason]) : reason;
}
